import { isTurnFailureCode, turnFailureMessage } from '@agentic-ivy/shared-types';
import type { UIMessage } from './messageParser';

const ERROR_PART = '[IVY_ERROR]\n';
const TURN_META_PART = '[TURN_META]\n';

export const errorPart = (message: string): string => ERROR_PART + message;

export const parseErrorPart = (text: string): string | null =>
  text.startsWith(ERROR_PART) ? text.slice(ERROR_PART.length) : null;

export const turnMetaPart = (meta: string): string => TURN_META_PART + meta;

const IMAGE_READ_NOTE = /^(?:\[Image: original \d+x\d+, displayed at \d+x\d+\. Multiply coordinates by \d+(?:\.\d+)? to map to original image\.\]\s*)+$/;

/**
 * The note a tool leaves after reading an image ("[Image: original WxH, displayed at WxH. Multiply coordinates by N
 * to map to original image.]"), stored as a user message. Only text that is nothing but such notes: a person's own
 * message that starts with "[Image" stays.
 */
export const isImageReadNote = (text: string): boolean => IMAGE_READ_NOTE.test(text.trim());

export const parseTurnMeta = (text: string): string | null =>
  text.startsWith(TURN_META_PART) ? text.slice(TURN_META_PART.length) : null;

/** A turn's time as the chat records it ("201.2s"), in words ("Took 3 min 21 s"); null when it isn't one. */
export function tookFor(meta: string | null): string | null {
  const sec = meta ? Number.parseFloat(meta) : Number.NaN;
  if (!Number.isFinite(sec) || !/^\d/.test(meta ?? '')) return null;
  const whole = Math.round(sec);
  return whole < 60 ? `Took ${whole} s` : `Took ${Math.floor(whole / 60)} min ${whole % 60} s`;
}

interface RefusalBody {
  error?: unknown;
  code?: unknown;
  retryAfterSec?: unknown;
}

const VENDOR_WORDS = /claude|anthropic|\b(?:opus|sonnet|haiku)\b|api error|exited with code/i;

interface StreamError {
  error?: unknown;
  code?: unknown;
  resetAt?: unknown;
}

/** The notice for a turn that stopped while it streamed: built from its code, a reset in the viewer's own time. */
export function streamErrorMessage(e: StreamError, partway: boolean): string {
  if (isTurnFailureCode(e.code)) {
    const resetAt = typeof e.resetAt === 'string' ? e.resetAt : null;
    return turnFailureMessage(e.code, { resetAt, yourTime: true, partway });
  }
  const said = typeof e.error === 'string' ? e.error.trim() : '';
  return said && !VENDOR_WORDS.test(said) ? said : turnFailureMessage('TURN_FAILED');
}

const MADE_SOMETHING = 'Everything made so far is saved';

const keyHasReset = (partway: boolean) => `Your model key hit its usage limit, and it has reset since. ${
  partway ? `${MADE_SOMETHING}; send a message to carry on.` : 'Send your message again to carry on.'
}`;

interface StoredRow {
  id?: string;
  role: string;
  content: string;
  isError: boolean;
  toolInput?: unknown;
  createdAt: string;
}

/**
 * A failed turn as read back from history: the notice the live stream showed. Only ivy-chat's failure row carries a code;
 * a run's own error result does not, and live it never was a notice either.
 */
export function storedFailureMessage(row: StoredRow, fallbackId: string): UIMessage | null {
  if (row.role !== 'result' || !row.isError) return null;
  const { code, resetAt } = (row.toolInput ?? {}) as StreamError;
  if (typeof code !== 'string') return null;
  const partway = row.content.includes(MADE_SOMETHING);
  const reset = code === 'MODEL_KEY_LIMIT' && typeof resetAt === 'string' ? Date.parse(resetAt) : Number.NaN;
  return {
    id: row.id || fallbackId,
    type: 'system',
    subtype: 'error',
    turnFailed: true,
    content: reset <= Date.now() ? keyHasReset(partway) : streamErrorMessage({ error: row.content, code, resetAt }, partway),
    timestamp: new Date(row.createdAt).getTime(),
  };
}

/** What to tell the user when the chat endpoint refuses a turn before any stream starts. */
export function refusalMessage(status: number, body: RefusalBody | null, retryAfterHeader: string | null): string {
  const code = typeof body?.code === 'string' ? body.code : null;
  if (code === 'CHAT_BUSY') return 'Still working on your last message. Wait for it to finish, or press Stop to start over.';
  if (code === 'CHAT_AT_CAPACITY') {
    const fromHeader = Number(retryAfterHeader);
    const seconds = Number.isFinite(fromHeader) && fromHeader > 0 ? fromHeader : Number(body?.retryAfterSec);
    const wait = Number.isFinite(seconds) && seconds > 0 ? `about ${Math.round(seconds)} seconds` : 'a minute';
    return `We're at capacity right now. Send your message again in ${wait}.`;
  }
  if (code === 'NO_HEALTHY_ACCOUNT') return turnFailureMessage('SERVICE_BUSY');
  if (typeof body?.error === 'string' && body.error.trim() && code && !VENDOR_WORDS.test(body.error)) return body.error;
  return `That message didn't go through (error ${status}). Try sending it again.`;
}
